import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  users: [],
  currentUser: null,
  isLoading: false,
  error: null,
  filters: {
    search: '',
    role: '',
    minCpdPoints: '',
    maxCpdPoints: '',
    email: '',
    name: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users with Filters
      .addCase('users/fetchUsersWithFilters/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('users/fetchUsersWithFilters/fulfilled', (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
      })
      .addCase('users/fetchUsersWithFilters/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch users';
      })
      // Get User by ID
      .addCase('users/getUserById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('users/getUserById/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload?.data || action.payload;
        state.error = null;
      })
      .addCase('users/getUserById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch user';
      })
      // Get User by ID (Admin - with stats and enrollments)
      .addCase('users/getUserByIdAdmin/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('users/getUserByIdAdmin/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload?.data || action.payload;
        state.error = null;
      })
      .addCase('users/getUserByIdAdmin/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch user details';
      })
      // Update User
      .addCase('users/updateUser/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('users/updateUser/fulfilled', (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex(user => user._id === action.payload._id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.currentUser && state.currentUser._id === action.payload._id) {
          state.currentUser = action.payload;
        }
        state.error = null;
      })
      .addCase('users/updateUser/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update user';
      });
  },
});

export const { 
  clearError, 
  clearCurrentUser, 
  setFilters, 
  setPagination, 
  setCurrentPage, 
  setItemsPerPage, 
  resetPagination 
} = userSlice.actions;

export default userSlice.reducer;

